import React from 'react';
import { PropagateLoader } from 'react-spinners';
import { CountryCode } from '@dayetopia/types';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import EmailCapture from '@components/shared/EmailCapture';
import {
  KLAVIYO_STI,
  KLAVIYO_VMS,
  KLAVIYO_HPV,
  KLAVIYO_VHB_IE,
  KLAVIYO_VHB_DE,
  KLAVIYO_STI_IE,
  KLAVIYO_STI_DE,
  KLAVIYO_HPV_IE,
  KLAVIYO_HPV_DE,
  KLAVIYO_VMS_DE,
  KLAVIYO_VMS_IE
} from '@constants';
import { ProductCode } from '@contracts';
import { useCountry } from '@hooks/useCountry';
import PaymentOptionsWaitlist from './PaymentOptionsWaitlist';
import ProductBuyOptions from './ProductBuyOptions';
import ProductPaymentBoxes from './ProductPaymentBoxes';
import ProductPaymentButton from './ProductPaymentButton';
import ProductPaymentOptions from './ProductPaymentOptions';
import { useShoppingContext } from './ProductShoppingContext';

const WaitlistPaymentRender: React.FC = () => {
  const { t } = useTranslation();
  const { price, productHeaderData, hasProductOptions, loaded } = useShoppingContext();
  const { country } = useCountry();
  const { klaviyoId, productCode, textColor, background } = productHeaderData;

  const renderEmailCapture = () => (
    <>
      <FirstToKnow $textColor={textColor}>{t('components.first-to-know')}</FirstToKnow>
      <EmailCapture id={klaviyoId} background={background} textColor={textColor} />
    </>
  );

  if (klaviyoId) {
    return renderEmailCapture();
  }

  const isVMSKit = productCode === ProductCode.ScreeningKit;
  const isSTI = productCode === ProductCode.STIScreening;
  const isHPV = productCode === ProductCode.HPVScreening;
  const isVaginalHealthBundle = productCode === ProductCode.VaginalHealthBundle;
  const isVMS = [ProductCode.VMSEssential, ProductCode.VMSAdvanced, ProductCode.VMSPremium].includes(productCode);

  const VMSAvailable = country === CountryCode.GB || country === CountryCode.US || country === CountryCode.IE;

  if (isVMSKit && !VMSAvailable) {
    return <PaymentOptionsWaitlist title="waitlist.vms" subtitle="waitlist.coming-eu" emailListId={KLAVIYO_VMS} />;
  }

  if (isVaginalHealthBundle && country === CountryCode.DE) {
    const emailListId = KLAVIYO_VHB_DE;
    return <PaymentOptionsWaitlist title="waitlist.vms" subtitle="waitlist.coming-eu" emailListId={emailListId} />;
  }

  if (isVMS && country === CountryCode.DE) {
    const emailListId = KLAVIYO_VMS_DE;
    return <PaymentOptionsWaitlist title="waitlist.vms" subtitle="waitlist.coming-eu" emailListId={emailListId} />;
  }

  if (isSTI && country !== CountryCode.GB && country !== CountryCode.IE) {
    let emailListId = KLAVIYO_STI;
    if (country === CountryCode.DE) {
      emailListId = KLAVIYO_STI_DE;
    }
    return <PaymentOptionsWaitlist title="waitlist.sti" subtitle="waitlist.coming-useu" emailListId={emailListId} />;
  }

  if (isHPV && country !== CountryCode.GB && country !== CountryCode.IE) {
    let emailListId = KLAVIYO_HPV;
    if (country === CountryCode.DE) {
      emailListId = KLAVIYO_HPV_DE;
    }
    return <PaymentOptionsWaitlist title="waitlist.hpv" subtitle="waitlist.coming-eu" emailListId={emailListId} />;
  }

  if (price === -1 || !loaded) {
    return (
      <LoadingWrap>
        <PropagateLoader color={textColor} />
      </LoadingWrap>
    );
  }

  // TODO: Check if we need product options for Proviotics
  if (hasProductOptions && productCode !== ProductCode.Proviotics) {
    return <ProductBuyOptions />;
  }

  return (
    <>
      <ProductPaymentBoxes />
      <ProductPaymentOptions />
      <ProductPaymentButton />
    </>
  );
};

export default WaitlistPaymentRender;

const FirstToKnow = styled.p<{ $textColor?: string; $breakpoint?: string }>`
  width: 100%;
  text-transform: uppercase;
  padding-bottom: 10px;
  font-family: BrownStd;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.75px;
  text-align: left;
  color: ${(props) => props.$textColor};

  @media (max-width: ${(props) => props.$breakpoint || '768px'}) {
    text-align: center;
  }
`;

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  min-height: 60px;
  justify-content: center;
  width: 100%;
`;
